import { useCallback, useState } from "react";
import { GrantPermission } from "admin/types/interfaces/grant";
import User from "admin/types/interfaces/user";
import { findUserByEmail, userPrincipal } from "admin/utils/users";
import { apiMutate, getApiErrorMessage, isApiError } from "~/utils/apiClient";

export interface ManagerRolesFlushResult {
  failedLabels: string[];
}

function setWithout(set: Set<string>, email: string): Set<string> {
  if (!set.has(email)) return set;
  const next = new Set(set);
  next.delete(email);
  return next;
}

function setWith(set: Set<string>, email: string): Set<string> {
  if (set.has(email)) return set;
  return new Set(set).add(email);
}

/**
 * Tracks pending Members-tab edits for a group: manager/member role changes and member
 * removals. Promotions (member → manager) and demotions (manager → member) are kept in
 * Sets keyed by email; a third Set holds members marked for removal. `getRoleFor` and
 * `isRemoved` merge these with the current server state to give the effective dropdown
 * state for display, and `flush` applies the changes to the grants API on Save, pruning
 * succeeded items so they don't re-appear as pending.
 *
 * Removal is modeled as a pending selection (the row stays visible until Save and can be
 * undone by re-picking Manager/Member), mirroring how the Administration tab treats a
 * "remove" choice. `setRole` clears any pending removal so switching back to a role undoes
 * it, and reconciles against the server role so re-selecting the original role records no
 * change.
 *
 * Intentionally separate from `usePendingGrants` (the Administration tab's pipeline): the
 * Members-tab role is coupled to group membership — "Remove" drops the user from the group
 * and a manager removed from the group must have can_manage revoked, which a grant-only
 * pipeline can't express (see `flush`'s removal handling). It is also a binary
 * manager/member toggle rather than graded view/edit/manage permissions, and promotions of
 * newly added members must POST only after the membership PUT lands. A small Set-based model
 * fits those constraints better than the addition/removal/update arrays.
 */
export function useManagerRoles(currentManagerEmails: Set<string>) {
  const [pendingPromotions, setPendingPromotions] = useState<Set<string>>(new Set());
  const [pendingDemotions, setPendingDemotions] = useState<Set<string>>(new Set());
  const [pendingRemovals, setPendingRemovals] = useState<Set<string>>(new Set());

  // currentManagerEmails holds lowercase ids (server-normalized grant principals), while
  // callers pass case-preserving user emails — fold before the membership test.
  const isManagerOnServer = (email: string) => currentManagerEmails.has(email.toLowerCase());

  const hasChanges =
    pendingPromotions.size > 0 || pendingDemotions.size > 0 || pendingRemovals.size > 0;

  const reset = useCallback(() => {
    setPendingPromotions(new Set());
    setPendingDemotions(new Set());
    setPendingRemovals(new Set());
  }, []);

  /** Effective role for a member, merging pending changes over the server state. */
  function getRoleFor(email: string): "manager" | "member" {
    if (pendingPromotions.has(email)) return "manager";
    if (pendingDemotions.has(email)) return "member";
    return isManagerOnServer(email) ? "manager" : "member";
  }

  /** Whether a member is pending removal from the group. */
  const isRemoved = (email: string): boolean => pendingRemovals.has(email);

  /**
   * Record an intended role change. Clears any pending removal (so switching back to a role
   * undoes a removal) and reconciles against the server role so re-selecting the current
   * role records no pending change.
   */
  const setRole = (email: string, role: "manager" | "member") => {
    setPendingRemovals((prev) => setWithout(prev, email));
    const isManager = isManagerOnServer(email);
    if (role === "manager") {
      setPendingDemotions((prev) => setWithout(prev, email));
      setPendingPromotions((prev) => (isManager ? setWithout(prev, email) : setWith(prev, email)));
    } else {
      setPendingPromotions((prev) => setWithout(prev, email));
      setPendingDemotions((prev) => (isManager ? setWith(prev, email) : setWithout(prev, email)));
    }
  };

  /** Mark a member for removal, superseding any pending role change. */
  const markRemoved = (email: string) => {
    setPendingRemovals((prev) => setWith(prev, email));
    setPendingPromotions((prev) => setWithout(prev, email));
    setPendingDemotions((prev) => setWithout(prev, email));
  };

  /** Drop all pending state for an email (call when an unsaved member is removed). */
  const clearRole = (email: string) => {
    setPendingPromotions((prev) => setWithout(prev, email));
    setPendingDemotions((prev) => setWithout(prev, email));
    setPendingRemovals((prev) => setWithout(prev, email));
  };

  /**
   * Apply all pending role changes to the grants API.
   *
   * Handles three cases:
   * - Removed members: revoke can_manage for any pending removal that held it
   * - Pending promotions: members being promoted from member to manager
   * - Pending demotions: members being demoted from manager to member
   *
   * `users` is used only to resolve display labels for error messages. Membership removals
   * themselves are persisted by the caller's group-users PUT; this only reconciles grants.
   * Prunes succeeded items so they don't re-appear on the next Save, and returns labels for
   * any operations that failed.
   */
  const flush = async (grantsUrl: string, users: User[]): Promise<ManagerRolesFlushResult> => {
    const failedLabels: string[] = [];
    const failedPromotions = new Set<string>();
    const failedDemotions = new Set<string>();
    const failedRemovals = new Set<string>();

    const labelFor = (email: string) => findUserByEmail(users, email)?.full_name || email;

    for (const email of pendingRemovals) {
      // Non-manager removals have no grant to revoke — membership PUT handles them.
      if (!isManagerOnServer(email)) continue;
      try {
        await apiMutate(
          grantsUrl,
          "DELETE",
          { principal: userPrincipal(email), permission: GrantPermission.can_manage },
          { expects204: true }
        );
      } catch (e) {
        const reason = isApiError(e) ? getApiErrorMessage(e) : "";
        failedLabels.push(reason ? `${labelFor(email)}: ${reason}` : labelFor(email));
        failedRemovals.add(email);
      }
    }

    for (const email of pendingPromotions) {
      try {
        await apiMutate(grantsUrl, "POST", {
          principal: userPrincipal(email),
          permission: GrantPermission.can_manage,
        });
      } catch (e) {
        const reason = isApiError(e) ? getApiErrorMessage(e) : "";
        failedLabels.push(reason ? `${labelFor(email)}: ${reason}` : labelFor(email));
        failedPromotions.add(email);
      }
    }

    for (const email of pendingDemotions) {
      try {
        await apiMutate(
          grantsUrl,
          "DELETE",
          { principal: userPrincipal(email), permission: GrantPermission.can_manage },
          { expects204: true }
        );
      } catch (e) {
        const reason = isApiError(e) ? getApiErrorMessage(e) : "";
        failedLabels.push(reason ? `${labelFor(email)}: ${reason}` : labelFor(email));
        failedDemotions.add(email);
      }
    }

    // Prune succeeded items so they don't re-appear as pending on the next Save
    setPendingPromotions(failedPromotions);
    setPendingDemotions(failedDemotions);
    setPendingRemovals(failedRemovals);

    return { failedLabels };
  };

  return {
    getRoleFor,
    isRemoved,
    setRole,
    markRemoved,
    clearRole,
    reset,
    hasChanges,
    flush,
    removedEmails: pendingRemovals,
  };
}
